import { create } from 'zustand'
import { TenantOnboardStep } from '../../TenantForm/constants/tenantOnboardEnums'
import { ONBOARDING_FLOW } from '../../TenantForm/constants/tenant-onboard-constants'
import {
    SetupBusiness,
    SetupPipelineStep,
    StageNavItem,
    StageProgressMap,
    TenantSetupStatusResponse,
    PosDetail,
    TenantDetail,
    BillingLevel,
    NotificationVendorGroup,
    EscalationEntityMap,
} from '../types/types'
import { ApiErrorResponse } from '../../TenantForm/types/types'
import { getSetupStatus, getLandingData, getCustomerSuccessOptions, executeOnboardStep, updateTenantSettings as apiUpdateTenantSettings } from '../services/api'
import {
    emptyEntityMap,
    getTenantEscalationConfig,
    updateTenantEscalationConfig,
} from '../services/escalationApi'
import {
    getTenantOirLockConfig,
    updateTenantOirLockConfig,
} from '../services/oirLockApi'
import {
    CustomerSuccessOption,
    OnboardingUserSummary,
    ModuleCatalogEntry,
    ModuleOwnerAssignment,
    OirFormLockConfig,
} from '../types/types'

const isLandingPath = (tenantId: number) =>
    window.location.pathname.replace(/\/+$/, '') === `/tenants/${tenantId}`

// ============================================================
// Subscription Pipeline Step Generator
// ============================================================

const generateSubscriptionPipeline = (
    businessId: number,
    businessName: string
): SetupPipelineStep[] => {
    const subscriptionStage = ONBOARDING_FLOW.find(
        (s) => s.stage === TenantOnboardStep.SUBSCRIPTION
    )
    return (subscriptionStage?.substeps ?? []).map((s) => ({
        stage: TenantOnboardStep.SUBSCRIPTION,
        substep: s.substep,
        label: `${s.stepName} — ${businessName}`,
        status: 'idle' as const,
        businessId,
        interactive: s.interactive === true,
    }))
}

// ============================================================
// Store Interface
// ============================================================

interface Tenant360Store {
    // Identity
    tenantId: number | null
    tenantName: string
    businesses: SetupBusiness[]
    metaData: TenantSetupStatusResponse['metaData'] | null

    // Navigation
    activeStage: TenantOnboardStep
    selectedBusinessId: number | null

    // Status from API
    completedSteps: string[]
    completedStages: string[]
    businessStepsProgress: StageProgressMap
    statusLoading: boolean
    statusError: string | null

    // Setup entities
    posDetails: PosDetail[]
    tenantDetails: TenantDetail | null

    // Landing-only data (from /landing). Preserved across lighter /status refreshes.
    tenantSubscribedModules: string[]
    businessSubscribedModules: Record<number, string[]>
    businessNewModules: Record<number, string[]>
    notificationVendors: NotificationVendorGroup[]
    onboardingUsers: OnboardingUserSummary[]
    moduleCatalog: ModuleCatalogEntry[]
    defaultOirModules: ModuleCatalogEntry[]
    onboardingForumLink: string | null

    // Subscription Pipeline
    subscriptionPipeline: SetupPipelineStep[]
    subscriptionPipelineRunning: boolean

    // Tenant escalation ladder. Its own endpoint (not part of /landing), but
    // shared state so the escalation card and the onboarding-users card — which
    // assigns levels to users — never disagree about what is configured.
    escalationEntities: EscalationEntityMap
    escalationLoading: boolean
    /** esKey the levels in state belong to; a different tenant forces a refetch */
    escalationKey: string | null
    escalationLoaded: boolean
    /** Set when the fetch failed — state holds no levels, not "no levels configured" */
    escalationError: string | null
    fetchEscalationConfig: (tenantEsKey: string, force?: boolean) => Promise<void>
    saveEscalationConfig: (
        tenantEsKey: string,
        entities: EscalationEntityMap
    ) => Promise<void>

    oirLock: OirFormLockConfig | null
    oirLockLoading: boolean
    oirLockKey: string | null
    oirLockLoaded: boolean
    oirLockError: string | null
    fetchOirLockConfig: (tenantEsKey: string, force?: boolean) => Promise<void>
    saveOirLockConfig: (tenantEsKey: string, lockAtUtc: string) => Promise<void>

    // CS / settings
    customerSuccessOptions: CustomerSuccessOption[]
    isAddBusinessOpen: boolean
    setIsAddBusinessOpen: (v: boolean) => void
    /** Lazily load CS dropdown options (only when the settings modal opens). */
    fetchCustomerSuccessOptions: () => Promise<void>
    updateTenantSettings: (payload: { billingLevel?: 'tenant' | 'business'; customerSuccess?: string | null; zohoCustomerName?: string | null }) => Promise<void>

    // Edit control
    allowEditDuringError: boolean
    toggleAllowEditDuringError: () => void

    // Change tracking (pipeline only runs when changes detected)
    hasSubscriptionChanges: boolean
    setSubscriptionChanged: (v: boolean) => void

    // Computed
    getStageNavItems: () => StageNavItem[]
    isStageComplete: (stage: TenantOnboardStep, businessId?: number) => boolean
    hasPipelineError: () => boolean

    // Actions
    // useLanding: use the heavier /landing endpoint (only the tenant landing page);
    // everything else defaults to the lighter /status.
    fetchStatus: (tenantId: number, useLanding?: boolean) => Promise<void>
    setActiveStage: (stage: TenantOnboardStep) => void
    selectBusiness: (businessId: number) => void
    runSubscriptionPipeline: (
        tenantId: number,
        businessId: number,
        businessName: string,
        startFromStep?: number
    ) => Promise<void>
    /**
     * Tenant-level variant:
     * 1. Calls SAVE_SUBSCRIPTION once with tenantId (no businessId).
     * 2. For each business runs all remaining subscription substeps.
     * The flat pipeline is stored in subscriptionPipeline so the same
     * bottom strip / retry logic works unchanged.
     */
    runTenantLevelSubscriptionPipeline: (
        tenantId: number,
        businesses: SetupBusiness[],
        startFromStep?: number
    ) => Promise<void>
    retrySubscriptionPipeline: () => Promise<void>
    submitModuleOwners: (
        assignments: ModuleOwnerAssignment[]
    ) => Promise<void>
    submitInteractiveStep: (
        payload: Record<string, unknown>
    ) => Promise<void>
    continueSubscriptionPipeline: (fromIndex: number) => Promise<void>
    revisitPreviousInteractiveStep: () => void
    setSubscriptionPipelineError: (
        businessId: number,
        businessName: string,
        error: ApiErrorResponse,
        businesses?: SetupBusiness[]
    ) => void
    reset: () => void
    resetPipeline: () => void
}

// ============================================================
// Stage Labels (reused from ONBOARDING_FLOW)
// ============================================================

const STAGE_LABELS: Record<string, string> = {}
ONBOARDING_FLOW.forEach((s) => {
    STAGE_LABELS[s.stage] = s.stageName
})

// ============================================================
// Initial State
// ============================================================

const initialState = {
    tenantId: null as number | null,
    tenantName: '',
    businesses: [] as SetupBusiness[],
    metaData: null as TenantSetupStatusResponse['metaData'] | null,
    activeStage: TenantOnboardStep.SUBSCRIPTION,
    selectedBusinessId: null as number | null,
    completedSteps: [] as string[],
    completedStages: [] as string[],
    businessStepsProgress: {} as StageProgressMap,
    statusLoading: true,
    statusError: null as string | null,
    posDetails: [] as PosDetail[],
    tenantDetails: null as TenantDetail | null,
    tenantSubscribedModules: [] as string[],
    businessSubscribedModules: {} as Record<number, string[]>,
    businessNewModules: {} as Record<number, string[]>,
    notificationVendors: [] as NotificationVendorGroup[],
    onboardingUsers: [] as OnboardingUserSummary[],
    moduleCatalog: [] as ModuleCatalogEntry[],
    defaultOirModules: [] as ModuleCatalogEntry[],
    onboardingForumLink: null as string | null,
    subscriptionPipeline: [] as SetupPipelineStep[],
    subscriptionPipelineRunning: false,
    allowEditDuringError: false,
    hasSubscriptionChanges: false,
    escalationEntities: emptyEntityMap(),
    escalationLoading: false,
    escalationKey: null as string | null,
    escalationLoaded: false,
    escalationError: null as string | null,
    oirLock: null as OirFormLockConfig | null,
    oirLockLoading: false,
    oirLockKey: null as string | null,
    oirLockLoaded: false,
    oirLockError: null as string | null,
    customerSuccessOptions: [] as CustomerSuccessOption[],
    isAddBusinessOpen: false,
}

// ============================================================
// Store
// ============================================================

const useTenant360Store = create<Tenant360Store>((set, get) => ({
    ...initialState,

    // ── Computed: Stage Nav Items ────────────────────────────
    getStageNavItems: (): StageNavItem[] => {
        const { completedStages, completedSteps, businessStepsProgress, tenantDetails, businesses } = get()
        const billingLevel = tenantDetails?.billingLevel?.toLowerCase()

        return ONBOARDING_FLOW.map((stageDef) => {
            const isComplete = completedStages.includes(stageDef.stage)

            let completedCount = 0
            let totalCount = 0

            stageDef.substeps.forEach((sub) => {
                // Progress-excluded steps are not reported by the status API
                if (sub.excludeFromProgress) return
                const isSaveSubscription = sub.substep === 'SAVE_SUBSCRIPTION'
                const isTenantLevelStep = !sub.isBusinessStep || (isSaveSubscription && billingLevel === 'tenant')

                if (isTenantLevelStep) {
                    const stepKey = `${stageDef.stage}:${sub.substep}`
                    if (completedSteps.includes(stepKey)) {
                        completedCount += 1
                    }
                    totalCount += 1
                } else {
                    const stageProgress = businessStepsProgress[stageDef.stage]
                    const bizList = businesses || []
                    if (bizList.length > 0) {
                        bizList.forEach((biz) => {
                            const bizProgress = stageProgress?.[String(biz.id)]
                            if (bizProgress?.completed.includes(sub.substep)) {
                                completedCount += 1
                            }
                            totalCount += 1
                        })
                    } else {
                        totalCount += 1
                    }
                }
            })

            let status: StageNavItem['status'] = 'upcoming'
            if (isComplete || (totalCount > 0 && completedCount === totalCount)) status = 'completed'
            else if (completedCount > 0) status = 'partial'

            return {
                stage: stageDef.stage,
                label: STAGE_LABELS[stageDef.stage] || stageDef.stageName,
                status,
                completedCount,
                totalCount,
            }
        })
    },

    isStageComplete: (stage, businessId) => {
        const { completedStages, businessStepsProgress } = get()
        if (completedStages.includes(stage)) return true
        if (businessId) {
            const progress = businessStepsProgress[stage]?.[String(businessId)]
            return progress ? progress.pending.length === 0 : false
        }
        return false
    },

    hasPipelineError: () => {
        return get().subscriptionPipeline.some((s) => s.status === 'error')
    },

    // ── Actions ─────────────────────────────────────────────

    setIsAddBusinessOpen: (v) => set({ isAddBusinessOpen: v }),

    fetchCustomerSuccessOptions: async () => {
        if (get().customerSuccessOptions.length > 0) return
        const opts = await getCustomerSuccessOptions()
        set({ customerSuccessOptions: opts })
    },

    // Both landing cards ask for this on mount; one fetch per tenant is enough.
    fetchEscalationConfig: async (tenantEsKey, force = false) => {
        if (!tenantEsKey) return
        const { escalationKey, escalationLoading, escalationLoaded } = get()
        const sameTenant = escalationKey === tenantEsKey
        if (!force && sameTenant && (escalationLoading || escalationLoaded)) return

        set({
            escalationLoading: true,
            escalationKey: tenantEsKey,
            escalationError: null,
            // Levels from another tenant must not linger while this one loads
            ...(sameTenant
                ? {}
                : { escalationEntities: emptyEntityMap(), escalationLoaded: false }),
        })
        try {
            const data = await getTenantEscalationConfig(tenantEsKey)
            set({
                escalationEntities: data?.entities ?? emptyEntityMap(),
                escalationLoaded: true,
                escalationError: null,
            })
        } catch (err: any) {
            // escalationLoaded stays false: nothing may be written over a
            // ladder that was never read back
            set({
                escalationEntities: emptyEntityMap(),
                escalationLoaded: false,
                escalationError:
                    err?.message || 'Failed to load escalation configurations',
            })
        } finally {
            set({ escalationLoading: false })
        }
    },

    saveEscalationConfig: async (tenantEsKey, entities) => {
        const saved = await updateTenantEscalationConfig(tenantEsKey, entities)
        set({
            escalationEntities: saved?.entities ?? entities,
            escalationKey: tenantEsKey,
            escalationLoaded: true,
            escalationError: null,
        })
    },

    fetchOirLockConfig: async (tenantEsKey, force = false) => {
        if (!tenantEsKey) return
        const { oirLockKey, oirLockLoading, oirLockLoaded } = get()
        const sameTenant = oirLockKey === tenantEsKey
        if (!force && sameTenant && (oirLockLoading || oirLockLoaded)) return

        set({
            oirLockLoading: true,
            oirLockKey: tenantEsKey,
            oirLockError: null,
            ...(sameTenant ? {} : { oirLock: null, oirLockLoaded: false }),
        })
        try {
            const data = await getTenantOirLockConfig(tenantEsKey)
            set({ oirLock: data, oirLockLoaded: true, oirLockError: null })
        } catch (err: any) {
            set({
                oirLock: null,
                oirLockLoaded: false,
                oirLockError:
                    err?.message || 'Failed to load the OIR form lock date',
            })
        } finally {
            set({ oirLockLoading: false })
        }
    },

    saveOirLockConfig: async (tenantEsKey, lockAtUtc) => {
        const saved = await updateTenantOirLockConfig(tenantEsKey, { lockAtUtc })
        set({
            oirLock: saved,
            oirLockKey: tenantEsKey,
            oirLockLoaded: true,
            oirLockError: null,
        })
    },

    updateTenantSettings: async (payload) => {
        const { tenantId } = get()
        if (!tenantId) return
        await apiUpdateTenantSettings(tenantId, payload)
        if (tenantId) {
            get().fetchStatus(tenantId, isLandingPath(tenantId))
        }
    },

    toggleAllowEditDuringError: () => {
        set({ allowEditDuringError: !get().allowEditDuringError })
    },

    setSubscriptionChanged: (v: boolean) => {
        set({ hasSubscriptionChanges: v })
    },

    fetchStatus: async (tenantId: number, useLanding = false) => {
        set({ statusLoading: true, statusError: null, tenantId })
        try {
            // The tenant landing page (/tenants/:tenantId) uses the combined
            // /landing endpoint (status + subscribed modules + notification vendors).
            // Sub-pages (e.g. subscriptions) use the lighter /status.
            // customerSuccessOptions is NOT loaded here — it's fetched lazily
            // via fetchCustomerSuccessOptions() when the settings modal opens.
            const status: TenantSetupStatusResponse = useLanding
                ? await getLandingData(tenantId)
                : await getSetupStatus(tenantId)

            const businesses = status.metaData?.businesses ?? []

            // Backend now returns billingLevel as "BUSINESS" | "TENANT".
            // Normalise to lowercase so all UI comparisons keep working.
            const tenantDetails: TenantDetail | null = status.tenantDetails
                ? {
                      ...status.tenantDetails,
                      billingLevel: status.tenantDetails.billingLevel
                          ? (String(status.tenantDetails.billingLevel).toLowerCase() as BillingLevel)
                          : undefined,
                  }
                : null

            // Determine active stage from nextFocus
            let activeStage = TenantOnboardStep.SUBSCRIPTION
            if (status.nextFocus?.stage) {
                activeStage =
                    (status.nextFocus.stage as TenantOnboardStep) ||
                    TenantOnboardStep.SUBSCRIPTION
            }

            // Subscribed modules + notification vendors + posDetails come only from
            // /landing. On a lighter /status refresh these are absent, so preserve
            // the previously loaded values instead of clearing them.
            const prev = get()
            const td = status.tenantDetails
            const hasModuleData = !!td &&
                (td.subscribedModules !== undefined ||
                    (td.businesses ?? []).some((b) => b.subscribedModules !== undefined))

            let tenantSubscribedModules = prev.tenantSubscribedModules
            let businessSubscribedModules = prev.businessSubscribedModules
            let businessNewModules = prev.businessNewModules
            if (hasModuleData) {
                tenantSubscribedModules = td?.subscribedModules ?? []
                businessSubscribedModules = {}
                businessNewModules = {}
                ;(td?.businesses ?? []).forEach((b) => {
                    businessSubscribedModules[b.id] = b.subscribedModules ?? []
                    businessNewModules[b.id] = b.newModuleKeys ?? []
                })
            }

            set({
                tenantId,
                businesses,
                metaData: status.metaData || null,
                completedSteps: status.completedSteps ?? [],
                completedStages: status.completedStages ?? [],
                businessStepsProgress: status.businessStepsProgress ?? {},
                posDetails: status.posDetails ?? prev.posDetails,
                tenantDetails,
                tenantSubscribedModules,
                businessSubscribedModules,
                businessNewModules,
                notificationVendors: status.notificationVendors ?? prev.notificationVendors,
                onboardingUsers: status.onboardingUsers ?? prev.onboardingUsers,
                moduleCatalog: status.moduleCatalog ?? prev.moduleCatalog,
                defaultOirModules:
                    status.defaultOirModules ?? prev.defaultOirModules,
                onboardingForumLink:
                    status.onboardingForumLink !== undefined
                        ? status.onboardingForumLink
                        : prev.onboardingForumLink,
                activeStage,
                selectedBusinessId: businesses[0]?.id ?? null,
                statusLoading: false,
            })
        } catch (err: any) {
            set({
                statusLoading: false,
                statusError: err?.message || 'Failed to load setup status',
            })
        }
    },

    setActiveStage: (stage) => set({ activeStage: stage }),

    selectBusiness: (businessId) => set({ selectedBusinessId: businessId }),

    runSubscriptionPipeline: async (
        tenantId,
        businessId,
        businessName,
        startFromStep = 0
    ) => {
        const steps = generateSubscriptionPipeline(businessId, businessName)

        // Mark steps before startFromStep as already succeeded
        for (let j = 0; j < startFromStep && j < steps.length; j++) {
            steps[j] = { ...steps[j], status: 'success' }
        }

        set({
            subscriptionPipeline: steps,
            subscriptionPipelineRunning: true,
            hasSubscriptionChanges: false,
        })

        for (let i = startFromStep; i < steps.length; i++) {
            if (steps[i].interactive) {
                set((state) => {
                    const updated = [...state.subscriptionPipeline]
                    updated[i] = { ...updated[i], status: 'awaiting-input' }
                    return {
                        subscriptionPipeline: updated,
                        subscriptionPipelineRunning: false,
                    }
                })
                return
            }

            // Mark running
            set((state) => {
                const updated = [...state.subscriptionPipeline]
                updated[i] = { ...updated[i], status: 'running' }
                return { subscriptionPipeline: updated }
            })

            try {
                await executeOnboardStep({
                    stage: TenantOnboardStep.SUBSCRIPTION,
                    substep: steps[i].substep,
                    tenantId,
                    businessId,
                })

                // Mark success
                set((state) => {
                    const updated = [...state.subscriptionPipeline]
                    updated[i] = { ...updated[i], status: 'success' }
                    return { subscriptionPipeline: updated }
                })
            } catch (err: any) {
                const apiError: ApiErrorResponse = {
                    message: err?.message || `Failed at ${steps[i].label}`,
                    nextSteps: err?.nextSteps || [
                        'Check the configuration and try again',
                    ],
                }
                set((state) => {
                    const updated = [...state.subscriptionPipeline]
                    updated[i] = {
                        ...updated[i],
                        status: 'error',
                        error: apiError,
                    }
                    return {
                        subscriptionPipeline: updated,
                        subscriptionPipelineRunning: false,
                    }
                })
                return // Stop on error
            }
        }

        set({ subscriptionPipelineRunning: false })

        const { tenantId: tid } = get()
        if (tid) {
            get().fetchStatus(tid, isLandingPath(tid))
        }
    },

    /**
     * Set the pipeline to show an error on SAVE_SUBSCRIPTION
     * without actually running the pipeline.
     * Used when the form submission itself fails.
     */
    setSubscriptionPipelineError: (businessId, businessName, error, businesses) => {
        let steps: SetupPipelineStep[] = []
        if (businesses && businesses.length > 0) {
            const subscriptionStage = ONBOARDING_FLOW.find(
                (s) => s.stage === TenantOnboardStep.SUBSCRIPTION
            )
            const allSubsteps = subscriptionStage?.substeps ?? []
            const [saveSubstep, ...remainingSubsteps] = allSubsteps
            steps = [
                {
                    stage: TenantOnboardStep.SUBSCRIPTION,
                    substep: saveSubstep.substep,
                    label: `${saveSubstep.stepName} (Tenant)`,
                    status: 'idle' as const,
                },
                ...businesses.flatMap((biz) =>
                    remainingSubsteps.map((s) => ({
                        stage: TenantOnboardStep.SUBSCRIPTION,
                        substep: s.substep,
                        label: `${s.stepName} — ${biz.name}`,
                        status: 'idle' as const,
                        businessId: biz.id,
                    }))
                ),
            ]
        } else {
            steps = generateSubscriptionPipeline(businessId, businessName)
        }
        // Mark first step as errored
        steps[0] = { ...steps[0], status: 'error', error }
        set({
            subscriptionPipeline: steps,
            subscriptionPipelineRunning: false,
        })
    },

    continueSubscriptionPipeline: async (fromIndex) => {
        const { tenantId } = get()
        if (!tenantId) return

        set({ subscriptionPipelineRunning: true })
        const steps = get().subscriptionPipeline

        for (let i = fromIndex; i < steps.length; i++) {
            if (steps[i].interactive) {
                set((state) => {
                    const updated = [...state.subscriptionPipeline]
                    updated[i] = { ...updated[i], status: 'awaiting-input' }
                    return {
                        subscriptionPipeline: updated,
                        subscriptionPipelineRunning: false,
                    }
                })
                return
            }

            set((state) => {
                const updated = [...state.subscriptionPipeline]
                updated[i] = { ...updated[i], status: 'running' }
                return { subscriptionPipeline: updated }
            })

            try {
                await executeOnboardStep({
                    stage: TenantOnboardStep.SUBSCRIPTION,
                    substep: steps[i].substep,
                    tenantId,
                    businessId: steps[i].businessId,
                })
                set((state) => {
                    const updated = [...state.subscriptionPipeline]
                    updated[i] = { ...updated[i], status: 'success' }
                    return { subscriptionPipeline: updated }
                })
            } catch (err: any) {
                const apiError: ApiErrorResponse = {
                    message: err?.message || `Failed at ${steps[i].label}`,
                    nextSteps: err?.nextSteps || [
                        'Check the configuration and try again',
                    ],
                }
                set((state) => {
                    const updated = [...state.subscriptionPipeline]
                    updated[i] = {
                        ...updated[i],
                        status: 'error',
                        error: apiError,
                    }
                    return {
                        subscriptionPipeline: updated,
                        subscriptionPipelineRunning: false,
                    }
                })
                return
            }
        }

        set({ subscriptionPipelineRunning: false })
        get().fetchStatus(tenantId, isLandingPath(tenantId))
    },

    submitModuleOwners: async (assignments) =>
        get().submitInteractiveStep({ assignments }),

    submitInteractiveStep: async (payload) => {
        const { tenantId, subscriptionPipeline } = get()
        const index = subscriptionPipeline.findIndex(
            (s) => s.status === 'awaiting-input'
        )
        if (index === -1 || !tenantId) return

        const step = subscriptionPipeline[index]

        set((state) => {
            const updated = [...state.subscriptionPipeline]
            updated[index] = {
                ...updated[index],
                status: 'running',
                error: undefined,
            }
            return {
                subscriptionPipeline: updated,
                subscriptionPipelineRunning: true,
            }
        })

        try {
            await executeOnboardStep({
                stage: TenantOnboardStep.SUBSCRIPTION,
                substep: step.substep,
                tenantId,
                businessId: step.businessId,
                ...payload,
            })
            set((state) => {
                const updated = [...state.subscriptionPipeline]
                updated[index] = { ...updated[index], status: 'success' }
                return { subscriptionPipeline: updated }
            })
        } catch (err: any) {
            const apiError: ApiErrorResponse = {
                message: err?.message || `Failed at ${step.label}`,
                nextSteps: err?.nextSteps || [
                    'Check the entered details and try again',
                ],
            }
            set((state) => {
                const updated = [...state.subscriptionPipeline]
                updated[index] = {
                    ...updated[index],
                    status: 'error',
                    error: apiError,
                }
                return {
                    subscriptionPipeline: updated,
                    subscriptionPipelineRunning: false,
                }
            })
            return
        }

        await get().fetchStatus(tenantId, true)
        await get().continueSubscriptionPipeline(index + 1)
    },

    revisitPreviousInteractiveStep: () => {
        const { subscriptionPipeline } = get()
        const current = subscriptionPipeline.findIndex(
            (s) => s.status === 'awaiting-input'
        )
        if (current === -1) return

        let target = -1
        for (let i = current - 1; i >= 0; i--) {
            if (subscriptionPipeline[i].interactive) {
                target = i
                break
            }
        }
        if (target === -1) return

        set((state) => {
            const updated = [...state.subscriptionPipeline]
            for (let i = target; i <= current; i++) {
                updated[i] = {
                    ...updated[i],
                    status: i === target ? 'awaiting-input' : 'idle',
                    error: undefined,
                }
            }
            return {
                subscriptionPipeline: updated,
                subscriptionPipelineRunning: false,
            }
        })
    },

    retrySubscriptionPipeline: async () => {
        const { subscriptionPipeline, tenantId } = get()
        const failedIndex = subscriptionPipeline.findIndex(
            (s) => s.status === 'error'
        )
        if (failedIndex === -1 || !tenantId) return

        const step = subscriptionPipeline[failedIndex]
        set({ subscriptionPipelineRunning: true })

        // Clear error
        set((state) => {
            const updated = [...state.subscriptionPipeline]
            updated[failedIndex] = {
                ...updated[failedIndex],
                status: 'running',
                error: undefined,
            }
            return { subscriptionPipeline: updated }
        })

        // Resume from failed step
        const steps = get().subscriptionPipeline
        for (let i = failedIndex; i < steps.length; i++) {
            if (steps[i].interactive) {
                set((state) => {
                    const updated = [...state.subscriptionPipeline]
                    updated[i] = { ...updated[i], status: 'awaiting-input' }
                    return {
                        subscriptionPipeline: updated,
                        subscriptionPipelineRunning: false,
                    }
                })
                return
            }

            if (i !== failedIndex) {
                set((state) => {
                    const updated = [...state.subscriptionPipeline]
                    updated[i] = { ...updated[i], status: 'running' }
                    return { subscriptionPipeline: updated }
                })
            }

            try {
                await executeOnboardStep({
                    stage: TenantOnboardStep.SUBSCRIPTION,
                    substep: steps[i].substep,
                    tenantId,
                    businessId: step.businessId,
                })

                set((state) => {
                    const updated = [...state.subscriptionPipeline]
                    updated[i] = { ...updated[i], status: 'success' }
                    return { subscriptionPipeline: updated }
                })
            } catch (err: any) {
                const apiError: ApiErrorResponse = {
                    message: err?.message || `Failed at ${steps[i].label}`,
                    nextSteps: err?.nextSteps || [
                        'Check the configuration and try again',
                    ],
                }
                set((state) => {
                    const updated = [...state.subscriptionPipeline]
                    updated[i] = {
                        ...updated[i],
                        status: 'error',
                        error: apiError,
                    }
                    return {
                        subscriptionPipeline: updated,
                        subscriptionPipelineRunning: false,
                    }
                })
                return
            }
        }

        set({ subscriptionPipelineRunning: false })
        if (tenantId) {
            get().fetchStatus(tenantId, isLandingPath(tenantId))
        }
    },

    runTenantLevelSubscriptionPipeline: async (tenantId, businesses, startFromStep = 0) => {
        const subscriptionStage = ONBOARDING_FLOW.find(
            (s) => s.stage === TenantOnboardStep.SUBSCRIPTION
        )
        const allSubsteps = subscriptionStage?.substeps ?? []
        const [saveSubstep, ...remainingSubsteps] = allSubsteps

        // Build flat pipeline: SAVE_SUBSCRIPTION (tenant-level) + per-business remaining
        const flatPipeline: SetupPipelineStep[] = [
            {
                stage: TenantOnboardStep.SUBSCRIPTION,
                substep: saveSubstep.substep,
                label: `${saveSubstep.stepName} (Tenant)`,
                status: 'idle' as const,
            },
            ...businesses.flatMap((biz) =>
                remainingSubsteps.map((s) => ({
                    stage: TenantOnboardStep.SUBSCRIPTION,
                    substep: s.substep,
                    label: `${s.stepName} — ${biz.name}`,
                    status: 'idle' as const,
                    businessId: biz.id,
                    interactive: s.interactive === true,
                }))
            ),
        ]

        // Mark steps before startFromStep as already succeeded
        for (let j = 0; j < startFromStep && j < flatPipeline.length; j++) {
            flatPipeline[j] = { ...flatPipeline[j], status: 'success' }
        }

        set({ subscriptionPipeline: flatPipeline, subscriptionPipelineRunning: true, hasSubscriptionChanges: false })

        // Step 0: tenant-level SAVE_SUBSCRIPTION (no businessId)
        if (startFromStep <= 0) {
            set((state) => {
                const updated = [...state.subscriptionPipeline]
                updated[0] = { ...updated[0], status: 'running' }
                return { subscriptionPipeline: updated }
            })
            try {
                await executeOnboardStep({
                    stage: TenantOnboardStep.SUBSCRIPTION,
                    substep: saveSubstep.substep,
                    tenantId,
                })
                set((state) => {
                    const updated = [...state.subscriptionPipeline]
                    updated[0] = { ...updated[0], status: 'success' }
                    return { subscriptionPipeline: updated }
                })
            } catch (err: any) {
                const apiError: ApiErrorResponse = {
                    message: err?.message || `Failed at ${saveSubstep.stepName}`,
                    nextSteps: err?.nextSteps || ['Check configuration and retry'],
                }
                set((state) => {
                    const updated = [...state.subscriptionPipeline]
                    updated[0] = { ...updated[0], status: 'error', error: apiError }
                    return { subscriptionPipeline: updated, subscriptionPipelineRunning: false }
                })
                return
            }
        }

        // Steps 1-N: per-business remaining substeps
        const pipeline = get().subscriptionPipeline
        const startIndex = Math.max(1, startFromStep)
        for (let i = startIndex; i < pipeline.length; i++) {
            if (pipeline[i].interactive) {
                set((state) => {
                    const updated = [...state.subscriptionPipeline]
                    updated[i] = { ...updated[i], status: 'awaiting-input' }
                    return {
                        subscriptionPipeline: updated,
                        subscriptionPipelineRunning: false,
                    }
                })
                return
            }
            set((state) => {
                const updated = [...state.subscriptionPipeline]
                updated[i] = { ...updated[i], status: 'running' }
                return { subscriptionPipeline: updated }
            })
            try {
                await executeOnboardStep({
                    stage: TenantOnboardStep.SUBSCRIPTION,
                    substep: pipeline[i].substep,
                    tenantId,
                    businessId: pipeline[i].businessId,
                })
                set((state) => {
                    const updated = [...state.subscriptionPipeline]
                    updated[i] = { ...updated[i], status: 'success' }
                    return { subscriptionPipeline: updated }
                })
            } catch (err: any) {
                const apiError: ApiErrorResponse = {
                    message: err?.message || `Failed at ${pipeline[i].label}`,
                    nextSteps: err?.nextSteps || ['Check configuration and retry'],
                }
                set((state) => {
                    const updated = [...state.subscriptionPipeline]
                    updated[i] = { ...updated[i], status: 'error', error: apiError }
                    return { subscriptionPipeline: updated, subscriptionPipelineRunning: false }
                })
                return
            }
        }

        set({ subscriptionPipelineRunning: false })
        const { tenantId: tid } = get()
        if (tid) {
            get().fetchStatus(tid, isLandingPath(tid))
        }
    },

    reset: () => set(initialState),
    resetPipeline: () =>
        set({
            subscriptionPipeline: [],
            subscriptionPipelineRunning: false,
            hasSubscriptionChanges: false,
        }),
}))

export default useTenant360Store
